import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Trash2, Save, ChevronLeft, ChevronRight, Download, Users, TrendingUp, Edit2, Info, DollarSign, X, Eye, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import { exportFixedExpensesToExcel } from './ExportExcelButton';
import { UnsavedChangesDialog, useUnsavedBlocker, BlockerDialog } from './UnsavedChangesDialog';
import { NumericInput, IntegerInput } from './NumericInput';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, 
  ComposedChart, Line, Area, AreaChart, LabelList
} from 'recharts';

const CATEGORIES = ['Personal', 'Oficina y Renta', 'IT e Infraestructura', 'Marketing', 'Servicios', 'Otros'];
const COLORS = {
  'Personal': '#3B82F6',
  'Oficina y Renta': '#10B981', 
  'IT e Infraestructura': '#94A3B8',
  'Marketing': '#F59E0B',
  'Servicios': '#8B5CF6',
  'Otros': '#EC4899'
};

// Custom label for stacked bar chart totals
const StackedBarTotalLabel = (props) => {
  const { x, y, width, index, data, categories, formatValue } = props;
  if (!data || index === undefined || !data[index]) return null;
  const entry = data[index];
  const total = categories.reduce((sum, cat) => sum + (entry[cat] || 0), 0);
  if (total === 0) return null;
  return (
    <text 
      x={x + width / 2} 
      y={y - 8} 
      fill="#94A3B8" 
      fontSize={11}
      fontWeight="600"
      textAnchor="middle"
    >
      {formatValue ? formatValue(total) : `$${(total/1000).toFixed(0)}k`}
    </text>
  );
};

// Waffle Chart component for expense concentration (similar to the reference image)
const TreemapChart = ({ data, total, inflation = 5, currency = 'MXN' }) => {
  if (!data || data.length === 0 || total === 0) return null;
  
  const sortedData = [...data]
    .filter(d => d.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .map(d => ({
      ...d,
      percentage: (d.amount / total * 100)
    }));

  // Calculate growth from previous data if available
  const getGrowth = (name) => {
    // This would come from comparing with previous year data
    // For now, return inflation rate as approximate growth
    return inflation || 5;
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      {sortedData.map((item, idx) => {
        const percentage = item.percentage;
        const filledSquares = Math.round(percentage / 4); // 25 squares = 100%
        const growth = getGrowth(item.name);
        const isPositive = growth >= 0;
        
        return (
          <div 
            key={item.name}
            className="bg-secondary/30 rounded-xl p-4 border border-border/30"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {item.name}
              </span>
              <span className={cn(
                "text-xs px-2 py-0.5 rounded",
                isPositive ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
              )}>
                {isPositive ? '↗' : '↘'} {Math.abs(growth).toFixed(0)}%
              </span>
            </div>
            
            {/* Percentage */}
            <div className="text-3xl font-bold mb-4" style={{ color: COLORS[item.name] }}>
              {percentage.toFixed(0)}%
            </div>
            
            {/* Waffle Grid - 5x5 = 25 squares */}
            <div className="grid grid-cols-5 gap-1 mb-3">
              {Array.from({ length: 25 }).map((_, i) => (
                <div 
                  key={i}
                  className={cn(
                    "aspect-square rounded-sm transition-all",
                    i < filledSquares ? "" : "opacity-20"
                  )}
                  style={{ 
                    backgroundColor: i < filledSquares ? COLORS[item.name] : '#374151'
                  }}
                />
              ))}
            </div>
            
            {/* Amount */}
            <div className="text-xs text-muted-foreground">
              {formatCurrency(item.amount, currency)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Personnel Edit Modal Component
const PersonnelEditModal = ({ isOpen, onClose, expense, onSave, years, inflation, currency, formatValue }) => {
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    if (expense) {
      setEditData({
        ...expense,
        headcount: expense.headcount || 1,
        monthly_amount: expense.monthly_amount || 0,
        growth_type: expense.growth_type || 'inflation',
        start_year: expense.start_year || 1,
        yearly_overrides: expense.yearly_overrides || {}
      });
    }
  }, [expense]);

  if (!editData) return null;

  const updateYearlyOverride = (year, field, value) => {
    setEditData(prev => ({
      ...prev,
      yearly_overrides: {
        ...prev.yearly_overrides,
        [year]: {
          ...(prev.yearly_overrides?.[year] || {}),
          [field]: field === 'headcount' ? parseInt(value) || 0 : parseFloat(value) || 0
        }
      }
    }));
  };

  // Calculate projection for this employee
  const calculateProjection = () => {
    const projection = [];
    const startYear = editData.start_year || 1;
    
    for (let year = 1; year <= years; year++) {
      if (year < startYear) {
        projection.push({
          year: `Año ${year}`,
          yearNum: year,
          headcount: 0,
          monthly_salary: 0,
          annual_cost: 0,
          avg_cost: 0,
          active: false
        });
        continue;
      }

      const yearsSinceStart = year - startYear;
      const override = editData.yearly_overrides?.[year] || {};
      
      // Get headcount for this year
      let headcount = override.headcount ?? editData.headcount ?? 1;
      
      // Get monthly salary for this year
      let monthlySalary;
      if (editData.growth_type === 'manual' && override.monthly_salary !== undefined) {
        monthlySalary = override.monthly_salary;
      } else if (editData.growth_type === 'fixed') {
        monthlySalary = editData.monthly_amount;
      } else {
        // inflation
        monthlySalary = editData.monthly_amount * Math.pow(1 + inflation / 100, yearsSinceStart);
      }

      const annualCost = monthlySalary * headcount * 12;
      const avgCost = headcount > 0 ? annualCost / headcount : 0;

      projection.push({
        year: `Año ${year}`,
        yearNum: year,
        headcount,
        monthly_salary: monthlySalary,
        annual_cost: annualCost,
        avg_cost: avgCost,
        active: true
      });
    }
    
    return projection;
  };

  const projection = calculateProjection();
  const totalCost = projection.reduce((sum, p) => sum + p.annual_cost, 0);
  const year1Cost = projection.find(p => p.yearNum === 1)?.annual_cost || 0;
  const lastYearCost = projection[projection.length - 1]?.annual_cost || 0;
  const n = years - 1;
  const cagr = year1Cost > 0 && n > 0 ? (Math.pow(lastYearCost / year1Cost, 1 / n) - 1) * 100 : 0;

  const handleSave = () => {
    // Convert yearly_overrides to the format expected by backend
    const updatedExpense = {
      ...editData,
      manual_amounts: {},
      headcount_by_year: {}
    };
    
    Object.entries(editData.yearly_overrides || {}).forEach(([year, data]) => {
      if (data.monthly_salary !== undefined) {
        updatedExpense.manual_amounts[year] = data.monthly_salary * (data.headcount ?? editData.headcount ?? 1) * 12;
      }
      if (data.headcount !== undefined) {
        updatedExpense.headcount_by_year[year] = data.headcount;
      }
    });
    
    onSave(updatedExpense);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <span className="text-xl">{editData.concept || 'Nueva Posición'}</span>
              <p className="text-sm text-muted-foreground font-normal">Configura el headcount y salario por año</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <Label>Nombre del Puesto</Label>
              <Input
                value={editData.concept}
                onChange={(e) => setEditData(prev => ({ ...prev, concept: e.target.value }))}
                placeholder="Ej: Gerente de Operaciones"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Headcount Base</Label>
              <Input
                type="number"
                min="1"
                value={editData.headcount}
                onChange={(e) => setEditData(prev => ({ ...prev, headcount: parseInt(e.target.value) || 1 }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Salario Mensual Base</Label>
              <Input
                type="number"
                value={editData.monthly_amount}
                onChange={(e) => setEditData(prev => ({ ...prev, monthly_amount: parseFloat(e.target.value) || 0 }))}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Inicia en</Label>
              <Select
                value={String(editData.start_year)}
                onValueChange={(v) => setEditData(prev => ({ ...prev, start_year: parseInt(v) }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: years }, (_, i) => (
                    <SelectItem key={i} value={String(i + 1)}>Año {i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Comportamiento Salarial</Label>
              <Select
                value={editData.growth_type}
                onValueChange={(v) => setEditData(prev => ({ ...prev, growth_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inflation">% Inflación ({inflation}%)</SelectItem>
                  <SelectItem value="fixed">Fijo (sin incremento)</SelectItem>
                  <SelectItem value="manual">Override Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="p-3 bg-primary/10 rounded-lg w-full text-center">
                <p className="text-xs text-muted-foreground">Costo Acumulado ({years} años)</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(totalCost, currency)}</p>
              </div>
            </div>
          </div>

          {/* KPI Summary */}
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Costo Año 1</p>
                <p className="text-lg font-bold">{formatCurrency(year1Cost, currency)}</p>
              </CardContent>
            </Card>
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Costo Año {years}</p>
                <p className="text-lg font-bold">{formatCurrency(lastYearCost, currency)}</p>
              </CardContent>
            </Card>
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">CAGR</p>
                <p className="text-lg font-bold text-primary">{cagr > 0 ? '+' : ''}{cagr.toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Headcount Max</p>
                <p className="text-lg font-bold text-blue-400">{Math.max(...projection.map(p => p.headcount))}</p>
              </CardContent>
            </Card>
          </div>

          {/* Yearly Override Table */}
          <div>
            <Label className="mb-3 block">Proyección y Override por Año (Valores Mensuales)</Label>
            <div className="border border-border rounded-lg overflow-hidden">
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="bg-blue-500/10">
                    <TableHead className="w-24">Año</TableHead>
                    <TableHead className="text-center">Headcount</TableHead>
                    <TableHead className="text-center">Salario Mensual</TableHead>
                    <TableHead className="text-right">Costo Anual</TableHead>
                    <TableHead className="text-right">Costo Promedio/Persona</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projection.map((p, idx) => {
                    const isActive = p.active;
                    const isManual = editData.growth_type === 'manual';
                    const override = editData.yearly_overrides?.[p.yearNum] || {};
                    
                    return (
                      <TableRow key={idx} className={cn(!isActive && "opacity-40")}>
                        <TableCell className="font-medium">{p.year}</TableCell>
                        <TableCell className="text-center">
                          {isActive && isManual ? (
                            <Input
                              type="number"
                              min="0"
                              value={override.headcount ?? editData.headcount ?? 1}
                              onChange={(e) => updateYearlyOverride(p.yearNum, 'headcount', e.target.value)}
                              className="w-20 mx-auto text-center"
                            />
                          ) : (
                            <span className="tabular-nums">{p.headcount}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {isActive && isManual ? (
                            <Input
                              type="number"
                              value={override.monthly_salary ?? Math.round(p.monthly_salary)}
                              onChange={(e) => updateYearlyOverride(p.yearNum, 'monthly_salary', e.target.value)}
                              className="w-28 mx-auto text-center"
                            />
                          ) : (
                            <span className="tabular-nums">{formatCurrency(p.monthly_salary, currency)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-blue-400">
                          {isActive ? formatCurrency(p.annual_cost, currency) : '-'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {isActive && p.headcount > 0 ? formatCurrency(p.avg_cost, currency) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total Row */}
                  <TableRow className="bg-blue-500/20 font-bold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-right text-blue-400">{formatCurrency(totalCost, currency)}</TableCell>
                    <TableCell className="text-right">-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Chart */}
          <div>
            <Label className="mb-3 block">Evolución del Costo de esta Posición</Label>
            <div className="h-48 border border-border rounded-lg p-4">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={projection.filter(p => p.active)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                  <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#3B82F6" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" stroke="#10B981" fontSize={11} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '12px' }}
                    formatter={(value, name) => {
                      if (name === 'headcount') return [value, 'Headcount'];
                      return [formatCurrency(value, currency), name === 'annual_cost' ? 'Costo Anual' : 'Salario Mensual'];
                    }}
                  />
                  <Bar yAxisId="left" dataKey="annual_cost" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                    <LabelList 
                      dataKey="annual_cost" 
                      position="top" 
                      formatter={(v) => formatValue ? formatValue(v) : `$${(v/1000).toFixed(0)}k`}
                      fill="#94A3B8"
                      fontSize={10}
                    />
                  </Bar>
                  <Line yAxisId="right" type="stepAfter" dataKey="headcount" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-4 mt-2 justify-center text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500" />
                <span>Costo Anual</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-500" />
                <span>Headcount</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} className="gap-2">
            <Save className="w-4 h-4" />
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FixedExpensesPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, updateProject, getCalculation } = useProject();
  const { showLabels, formatChartValue, formatTableValue } = useChartPreferences();
  
  const [expenses, setExpenses] = useState([]);
  const [expenseProjection, setExpenseProjection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('inputs');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [initialExpenses, setInitialExpenses] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [personnelView, setPersonnelView] = useState('annual');

  useEffect(() => {
    const loadData = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await fetchProject(projectId);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject]);

  useEffect(() => {
    if (currentProject?.fixed_expenses) {
      // Ensure all personnel expenses have headcount initialized
      const processedExpenses = currentProject.fixed_expenses.map(e => ({
        ...e,
        headcount: e.category === 'Personal' ? (e.headcount || 1) : (e.headcount || 0),
        headcount_by_year: e.headcount_by_year || {}
      }));
      setExpenses(processedExpenses.length > 0 ? processedExpenses : []);
      setInitialExpenses(JSON.stringify(processedExpenses));
    }
  }, [currentProject]);

  useEffect(() => {
    const loadProjection = async () => {
      if (activeTab === 'summary' && expenses.some(e => e.monthly_amount > 0)) {
        try {
          const data = await getCalculation(projectId, 'expenses');
          setExpenseProjection(data);
        } catch (err) {
          console.error('Error loading expense projection:', err);
        }
      }
    };
    loadProjection();
  }, [activeTab, expenses, projectId, getCalculation]);

  const addExpense = (category = 'Otros') => {
    const newExpense = {
      id: crypto.randomUUID(),
      category,
      concept: '',
      monthly_amount: 0,
      growth_type: 'inflation',
      manual_amounts: {},
      start_year: 1,
      headcount: category === 'Personal' ? 1 : 0,
      headcount_by_year: {},
      yearly_overrides: {}
    };
    
    if (category === 'Personal') {
      setEditingExpense(newExpense);
      setEditModalOpen(true);
    }
    
    setExpenses(prev => [...prev, newExpense]);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setEditModalOpen(true);
  };

  const saveExpenseFromModal = (updatedExpense) => {
    setExpenses(prev => prev.map(e => 
      e.id === updatedExpense.id ? updatedExpense : e
    ));
  };

  const removeExpense = (id) => {
    setExpenses(prev => prev.filter(e => e.id !== id));
  };

  const updateExpense = (id, field, value) => {
    setExpenses(prev => prev.map(e => 
      e.id === id ? { ...e, [field]: value } : e
    ));
  };

  const updateManualAmount = (id, year, value) => {
    setExpenses(prev => prev.map(e => {
      if (e.id === id) {
        return {
          ...e,
          manual_amounts: { ...e.manual_amounts, [year]: parseFloat(value) || 0 }
        };
      }
      return e;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validExpenses = expenses.filter(e => e.concept.trim());
      await updateProject(projectId, { fixed_expenses: validExpenses });
      setInitialExpenses(JSON.stringify(expenses));
      toast.success('Gastos fijos guardados');
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = initialExpenses && JSON.stringify(expenses) !== initialExpenses;

  const { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel } = useUnsavedBlocker(hasUnsavedChanges, handleSave);

  const goToNext = () => {
    if (!tryNavigate(`/app/project/${projectId}/income-statement`)) {
      navigate(`/app/project/${projectId}/income-statement`);
    }
  };

  const goToPrev = () => {
    if (!tryNavigate(`/app/project/${projectId}/pricing`)) {
      navigate(`/app/project/${projectId}/pricing`);
    }
  };

  const years = currentProject?.specifications?.project_life_years || 5;
  const currency = currentProject?.specifications?.currency || 'MXN';
  const inflation = currentProject?.financing?.inflation_rate || 4;
  
  const personnelExpenses = expenses.filter(e => e.category === 'Personal');
  const otherExpenses = expenses.filter(e => e.category !== 'Personal');
  
  const totalMonthlyExpenses = expenses.reduce((sum, e) => sum + (parseFloat(e.monthly_amount) || 0) * (e.category === 'Personal' ? (e.headcount || 1) : 1), 0);
  const totalAnnualExpenses = totalMonthlyExpenses * 12;
  // Fix: Use || 1 as default for personnel headcount since each position has at least 1 person
  const totalHeadcount = personnelExpenses.reduce((sum, e) => sum + (parseInt(e.headcount) || 1), 0);

  // Calculate yearly total
  const calculateYearlyTotal = (year) => {
    return expenses.reduce((sum, e) => {
      const startYear = e.start_year || 1;
      if (year < startYear) return sum;
      
      const yearsSinceStart = year - startYear;
      const headcount = e.category === 'Personal' ? (e.headcount_by_year?.[year] ?? e.headcount ?? 1) : 1;
      let amount = e.monthly_amount * headcount * 12;
      
      if (e.growth_type === 'inflation') {
        amount = amount * Math.pow(1 + inflation / 100, yearsSinceStart);
      } else if (e.growth_type === 'manual' && e.manual_amounts?.[year]) {
        amount = e.manual_amounts[year];
      }
      
      return sum + amount;
    }, 0);
  };

  // Category data for treemap
  const accumulatedByCategory = CATEGORIES.map(cat => {
    let total = 0;
    for (let y = 1; y <= years; y++) {
      expenses.filter(e => e.category === cat).forEach(e => {
        const startYear = e.start_year || 1;
        if (y >= startYear) {
          const yearsSinceStart = y - startYear;
          const headcount = e.category === 'Personal' ? (e.headcount_by_year?.[y] ?? e.headcount ?? 1) : 1;
          let amount = e.monthly_amount * headcount * 12;
          if (e.growth_type === 'inflation') {
            amount = amount * Math.pow(1 + inflation / 100, yearsSinceStart);
          } else if (e.growth_type === 'manual' && e.manual_amounts?.[y]) {
            amount = e.manual_amounts[y];
          }
          total += amount;
        }
      });
    }
    return { name: cat, amount: total };
  }).filter(d => d.amount > 0);

  const totalAccumulated = accumulatedByCategory.reduce((sum, d) => sum + d.amount, 0);

  // Yearly breakdown
  const yearlyBreakdown = Array.from({ length: years }, (_, i) => {
    const year = i + 1;
    const yearData = { year: `Año ${year}` };
    
    CATEGORIES.forEach(cat => {
      let catTotal = 0;
      expenses.filter(e => e.category === cat).forEach(e => {
        const startYear = e.start_year || 1;
        if (year >= startYear) {
          const yearsSinceStart = year - startYear;
          const headcount = e.category === 'Personal' ? (e.headcount_by_year?.[year] ?? e.headcount ?? 1) : 1;
          let amount = e.monthly_amount * headcount * 12;
          if (e.growth_type === 'inflation') {
            amount = amount * Math.pow(1 + inflation / 100, yearsSinceStart);
          } else if (e.growth_type === 'manual' && e.manual_amounts?.[year]) {
            amount = e.manual_amounts[year];
          }
          catTotal += amount;
        }
      });
      yearData[cat] = catTotal;
    });
    
    return yearData;
  });

  // Personnel metrics
  const personnelMetrics = Array.from({ length: years }, (_, i) => {
    const year = i + 1;
    let totalCost = 0;
    let totalHeads = 0;
    
    personnelExpenses.forEach(e => {
      const startYear = e.start_year || 1;
      if (year >= startYear) {
        const yearsSinceStart = year - startYear;
        // Use headcount from headcount_by_year first, then base headcount, default to 1
        const heads = e.headcount_by_year?.[year] ?? e.headcount ?? 1;
        let amount = (e.monthly_amount || 0) * heads * 12;
        if (e.growth_type === 'inflation') {
          amount = amount * Math.pow(1 + inflation / 100, yearsSinceStart);
        } else if (e.growth_type === 'manual' && e.manual_amounts?.[year]) {
          amount = e.manual_amounts[year];
        }
        totalCost += amount;
        totalHeads += heads;
      }
    });
    
    return {
      year: `Año ${year}`,
      'Costo Total': totalCost,
      'Headcount': totalHeads,
      'Costo Promedio': totalHeads > 0 ? totalCost / totalHeads : 0
    };
  });

  const filteredExpenses = selectedCategory === 'all' 
    ? expenses 
    : expenses.filter(e => e.category === selectedCategory);

  const calculateCagr = (cat) => {
    const year1 = yearlyBreakdown[0]?.[cat] || 0;
    const yearLast = yearlyBreakdown[years - 1]?.[cat] || 0;
    const n = years - 1;
    if (year1 <= 0 || n <= 0) return 0;
    return (Math.pow(yearLast / year1, 1 / n) - 1) * 100;
  };

  // Calculate personnel cost for display in table
  const getPersonnelYearCost = (expense, year) => {
    const startYear = expense.start_year || 1;
    if (year < startYear) return { cost: 0, headcount: 0 };
    
    const yearsSinceStart = year - startYear;
    const headcount = expense.headcount_by_year?.[year] ?? expense.headcount ?? 1;
    let monthlySalary = expense.monthly_amount;
    
    if (expense.growth_type === 'inflation') {
      monthlySalary = monthlySalary * Math.pow(1 + inflation / 100, yearsSinceStart);
    } else if (expense.growth_type === 'manual') {
      const override = expense.yearly_overrides?.[year];
      if (override?.monthly_salary !== undefined) {
        monthlySalary = override.monthly_salary;
      }
    }
    
    return { cost: monthlySalary * headcount * 12, headcount };
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fixed-expenses-page">
      {/* Personnel Edit Modal */}
      <PersonnelEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        expense={editingExpense}
        onSave={saveExpenseFromModal}
        years={years}
        inflation={inflation}
        currency={currency}
        formatValue={formatChartValue}
      />

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 7 DE 10</span>
          </div>
          <h1 className="text-4xl font-bold">Gastos Fijos</h1>
          <p className="text-muted-foreground mt-1">
            Gestión de costos operativos fijos incluyendo personal, renta, y servicios
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => exportFixedExpensesToExcel(
              expenses, 
              currentProject?.name || 'Proyecto',
              currentProject?.specifications?.project_life_years || 5,
              currency
            )}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="inputs">INPUTS</TabsTrigger>
          <TabsTrigger value="summary">RESUMEN</TabsTrigger>
        </TabsList>

        <TabsContent value="inputs" className="mt-6 space-y-6">
          {/* Personal Section */}
          <Card className="border-blue-500/30">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <CardTitle>Personal y Nómina</CardTitle>
                    <p className="text-sm text-muted-foreground">Haz clic en una posición para editar detalles</p>
                  </div>
                </div>
                <Button onClick={() => addExpense('Personal')} size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar Posición
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {personnelExpenses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-blue-500/10">
                      <TableHead>POSICIÓN / PUESTO</TableHead>
                      <TableHead className="text-center w-24">HEADCOUNT</TableHead>
                      <TableHead className="text-right w-36">SALARIO MENSUAL</TableHead>
                      <TableHead className="text-center w-28">INICIA EN</TableHead>
                      <TableHead className="text-center w-32">COMPORTAMIENTO</TableHead>
                      <TableHead className="text-right w-36">COSTO ANUAL (Año 1)</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {personnelExpenses.map((expense) => (
                      <TableRow key={expense.id} className="group cursor-pointer hover:bg-blue-500/5" onClick={() => openEditModal(expense)}>
                        <TableCell className="font-medium">
                          {expense.concept || <span className="text-muted-foreground italic">Sin nombre</span>}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{expense.headcount || 1}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(expense.monthly_amount || 0, currency)}</TableCell>
                        <TableCell className="text-center">Año {expense.start_year || 1}</TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "px-2 py-1 rounded text-xs",
                            expense.growth_type === 'inflation' && "bg-primary/20 text-primary",
                            expense.growth_type === 'fixed' && "bg-secondary text-muted-foreground",
                            expense.growth_type === 'manual' && "bg-warning/20 text-warning"
                          )}>
                            {expense.growth_type === 'inflation' && `+${inflation}%`}
                            {expense.growth_type === 'fixed' && 'Fijo'}
                            {expense.growth_type === 'manual' && 'Manual'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-blue-400">
                          {formatCurrency((expense.monthly_amount || 0) * (expense.headcount || 1) * 12, currency)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); openEditModal(expense); }}
                            >
                              <Edit2 className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); removeExpense(expense.id); }}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay gastos de personal configurados</p>
                  <Button onClick={() => addExpense('Personal')} variant="outline" size="sm" className="mt-3">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Primera Posición
                  </Button>
                </div>
              )}
              {personnelExpenses.length > 0 && (
                <div className="flex items-center justify-between p-4 bg-blue-500/10 border-t border-border">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Total Headcount (Año 1)</p>
                      <p className="text-2xl font-bold text-blue-400">{totalHeadcount}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground uppercase">Costo de Personal Anual (Año 1)</p>
                    <p className="text-2xl font-bold text-blue-400">
                      {formatCurrency(personnelExpenses.reduce((s, e) => s + (e.monthly_amount || 0) * (e.headcount || 1) * 12, 0), currency)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Other Expenses Section */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <CardTitle>Otros Gastos Fijos</CardTitle>
                    <p className="text-sm text-muted-foreground">Renta, servicios, marketing, etc.</p>
                  </div>
                </div>
                <Button onClick={() => addExpense('Otros')} size="sm" variant="outline" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar Gasto
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {otherExpenses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/30">
                      <TableHead className="w-44">CATEGORÍA</TableHead>
                      <TableHead>CONCEPTO</TableHead>
                      <TableHead className="text-right w-36">MONTO MENSUAL</TableHead>
                      <TableHead className="text-center w-28">INICIA EN</TableHead>
                      <TableHead className="text-center w-32">COMPORTAMIENTO</TableHead>
                      <TableHead className="text-right w-36">MONTO ANUAL</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {otherExpenses.map((expense) => (
                      <React.Fragment key={expense.id}>
                        <TableRow className="group">
                          <TableCell>
                            <Select 
                              value={expense.category} 
                              onValueChange={(v) => updateExpense(expense.id, 'category', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CATEGORIES.filter(c => c !== 'Personal').map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder="Ej: Renta de oficina"
                              value={expense.concept}
                              onChange={(e) => updateExpense(expense.id, 'concept', e.target.value)}
                              className="bg-transparent border-0 focus-visible:ring-1"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              placeholder="0"
                              value={expense.monthly_amount || ''}
                              onChange={(e) => updateExpense(expense.id, 'monthly_amount', parseFloat(e.target.value) || 0)}
                              className="w-28 ml-auto text-right tabular-nums"
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Select
                              value={String(expense.start_year || 1)}
                              onValueChange={(v) => updateExpense(expense.id, 'start_year', parseInt(v))}
                            >
                              <SelectTrigger className="w-24 mx-auto">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: years }, (_, i) => (
                                  <SelectItem key={i} value={String(i + 1)}>Año {i + 1}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-center">
                            <Select
                              value={expense.growth_type}
                              onValueChange={(v) => updateExpense(expense.id, 'growth_type', v)}
                            >
                              <SelectTrigger className="w-28 mx-auto">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inflation">% Inflación</SelectItem>
                                <SelectItem value="fixed">Fijo</SelectItem>
                                <SelectItem value="manual">Manual</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {formatCurrency((expense.monthly_amount || 0) * 12, currency)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeExpense(expense.id)}
                              className="opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expense.growth_type === 'manual' && (
                          <TableRow className="bg-secondary/20">
                            <TableCell colSpan={7} className="py-3">
                              <div className="flex items-center gap-4 px-4">
                                <span className="text-xs text-muted-foreground w-32">Override anual:</span>
                                <div className="flex gap-3 flex-1">
                                  {Array.from({ length: years }, (_, i) => {
                                    const year = i + 1;
                                    const startYear = expense.start_year || 1;
                                    if (year < startYear) return null;
                                    return (
                                      <div key={i} className="flex flex-col items-center gap-1">
                                        <span className="text-xs text-muted-foreground">Año {year}</span>
                                        <Input
                                          type="number"
                                          placeholder={String(Math.round(expense.monthly_amount * 12 * Math.pow(1 + inflation / 100, year - startYear)))}
                                          value={expense.manual_amounts?.[year] ?? ''}
                                          onChange={(e) => updateManualAmount(expense.id, year, e.target.value)}
                                          className="w-24 text-center text-xs"
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay otros gastos fijos configurados</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Total Summary */}
          <Card className="bg-primary/5 border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase">Total Gastos Fijos Mensuales</p>
                  <p className="text-3xl font-bold">{formatCurrency(totalMonthlyExpenses, currency)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground uppercase">Total Gastos Fijos Anuales (Año 1)</p>
                  <p className="text-4xl font-bold text-primary">{formatCurrency(totalAnnualExpenses, currency)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="summary" className="mt-6 space-y-6">
          {/* Global Filter */}
          <div className="flex justify-end">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Todos los rubros" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los rubros</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="kpi-card">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Total Gastos (Año 1)</p>
                <p className="text-2xl font-bold">{formatCurrency(calculateYearlyTotal(1), currency)}</p>
              </CardContent>
            </Card>
            <Card className="kpi-card">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Total Gastos (Año {years})</p>
                <p className="text-2xl font-bold">{formatCurrency(calculateYearlyTotal(years), currency)}</p>
              </CardContent>
            </Card>
            <Card className="kpi-card">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Headcount Total (Año 1)</p>
                <p className="text-2xl font-bold text-blue-400">{totalHeadcount} personas</p>
              </CardContent>
            </Card>
            <Card className="kpi-card">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">CAGR Gastos Totales</p>
                <p className="text-2xl font-bold text-primary">
                  {(() => {
                    const y1 = calculateYearlyTotal(1);
                    const yL = calculateYearlyTotal(years);
                    const n = years - 1;
                    const cagr = y1 > 0 && n > 0 ? (Math.pow(yL / y1, 1 / n) - 1) * 100 : 0;
                    return `${cagr > 0 ? '+' : ''}${cagr.toFixed(1)}%`;
                  })()}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Stacked Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Desglose Anual de Gastos Fijos</CardTitle>
              <p className="text-sm text-muted-foreground">Asignación de costos apilada por categoría</p>
            </CardHeader>
            <CardContent>
              {yearlyBreakdown.length > 0 && expenses.some(e => e.monthly_amount > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={yearlyBreakdown} barCategoryGap="20%" margin={{ top: 25, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                      formatter={(value) => formatCurrency(value, currency)}
                    />
                    <Legend />
                    {(() => {
                      const filteredCategories = CATEGORIES.filter(cat => selectedCategory === 'all' || cat === selectedCategory);
                      const renderLabel = (props) => (
                        <StackedBarTotalLabel 
                          {...props} 
                          data={yearlyBreakdown} 
                          categories={filteredCategories}
                          formatValue={formatChartValue}
                        />
                      );
                      return filteredCategories.map((cat, idx, arr) => (
                        <Bar key={cat} dataKey={cat} stackId="a" fill={COLORS[cat]}>
                          {showLabels && idx === arr.length - 1 && (
                            <LabelList content={renderLabel} />
                          )}
                        </Bar>
                      ));
                    })()}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Agrega gastos para ver el desglose
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            {/* Personnel Combined Chart */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-500" />
                      Métricas de Personal
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">Costo total, costo promedio por persona y headcount</p>
                  </div>
                  <div className="flex gap-1 bg-secondary/50 rounded-lg p-1">
                    <Button size="sm" variant={personnelView === 'annual' ? 'default' : 'ghost'} className="h-7 text-xs px-3"
                      onClick={() => setPersonnelView('annual')}>Anual</Button>
                    <Button size="sm" variant={personnelView === 'monthly' ? 'default' : 'ghost'} className="h-7 text-xs px-3"
                      onClick={() => setPersonnelView('monthly')}>Mensual</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {personnelMetrics.some(m => m['Costo Total'] > 0) ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={personnelMetrics.map(m => {
                      const divisor = personnelView === 'monthly' ? 12 : 1;
                      return {
                        ...m,
                        'Costo Total': m['Costo Total'] / divisor,
                        'Costo Promedio': m['Costo Promedio'] / divisor
                      };
                    })} barGap={2} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                      <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                      <YAxis 
                        yAxisId="left" 
                        stroke="#3B82F6" 
                        fontSize={11} 
                        tickFormatter={(v) => formatChartValue(v)}
                        domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        stroke="#10B981" 
                        fontSize={11}
                        domain={[0, (dataMax) => Math.ceil(dataMax * 1.5) || 5]}
                        allowDecimals={false}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                        formatter={(value, name) => {
                          if (name === 'Headcount') return [value, name];
                          return [formatCurrency(value, currency), `${name} (${personnelView === 'monthly' ? 'Mensual' : 'Anual'})`];
                        }}
                      />
                      <Legend formatter={(value) => value === 'Headcount' ? value : `${value} (${personnelView === 'monthly' ? '/mes' : '/año'})`} />
                      <Bar yAxisId="left" dataKey="Costo Total" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={30}>
                        {showLabels && (
                          <LabelList 
                            dataKey="Costo Total"
                            position="top" 
                            formatter={(v) => formatChartValue(v)}
                            fill="#3B82F6"
                            fontSize={9}
                          />
                        )}
                      </Bar>
                      <Bar yAxisId="left" dataKey="Costo Promedio" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={30}>
                        {showLabels && (
                          <LabelList 
                            dataKey="Costo Promedio"
                            position="top" 
                            formatter={(v) => formatChartValue(v)}
                            fill="#F59E0B"
                            fontSize={9}
                          />
                        )}
                      </Bar>
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="Headcount" 
                        stroke="#10B981" 
                        strokeWidth={3} 
                        dot={{ fill: '#10B981', r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    Agrega gastos de personal
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Treemap Concentration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Concentración de Gastos</CardTitle>
                  <span className="px-2 py-1 bg-warning/20 text-warning text-xs rounded">% ACUMULADO TOTAL</span>
                </div>
                <p className="text-sm text-muted-foreground">Distribución sobre el total acumulado del proyecto ({years} años)</p>
              </CardHeader>
              <CardContent>
                {accumulatedByCategory.length > 0 ? (
                  <TreemapChart 
                    data={accumulatedByCategory} 
                    total={totalAccumulated} 
                    inflation={currentProject?.financing?.inflation_rate || 5}
                    currency={currency}
                  />
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    Agrega gastos para ver la concentración
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Verification Table */}
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-primary" />
                Tabla de Verificación - Gastos por Rubro/Año
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Resumen detallado con CAGR por categoría
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow className="bg-primary/10">
                      <TableHead className="font-bold text-primary">Categoría</TableHead>
                      <TableHead className="font-bold text-primary">Concepto</TableHead>
                      {Array.from({ length: years }, (_, i) => (
                        <TableHead key={i} className="text-center font-bold text-primary">
                          Año {i + 1}
                        </TableHead>
                      ))}
                      <TableHead className="text-center font-bold text-primary bg-primary/20">CAGR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExpenses.map((expense, idx) => {
                      const startYear = expense.start_year || 1;
                      const amounts = Array.from({ length: years }, (_, i) => {
                        const year = i + 1;
                        if (year < startYear) return 0;
                        const yearsSinceStart = year - startYear;
                        const headcount = expense.category === 'Personal' ? (expense.headcount_by_year?.[year] ?? expense.headcount ?? 1) : 1;
                        let amount = (expense.monthly_amount || 0) * headcount * 12;
                        if (expense.growth_type === 'inflation') {
                          amount = amount * Math.pow(1 + inflation / 100, yearsSinceStart);
                        } else if (expense.growth_type === 'manual' && expense.manual_amounts?.[year]) {
                          amount = expense.manual_amounts[year];
                        }
                        return amount;
                      });
                      
                      const firstNonZero = amounts.find(a => a > 0) || 0;
                      const lastAmount = amounts[amounts.length - 1] || 0;
                      const yearsActive = amounts.filter(a => a > 0).length - 1;
                      const cagr = firstNonZero > 0 && yearsActive > 0 
                        ? (Math.pow(lastAmount / firstNonZero, 1 / yearsActive) - 1) * 100 
                        : 0;

                      return (
                        <TableRow key={expense.id} className={idx % 2 === 0 ? 'bg-secondary/20' : ''}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[expense.category] }} />
                              {expense.category}
                            </div>
                          </TableCell>
                          <TableCell>
                            {expense.concept || 'Sin nombre'}
                            {expense.category === 'Personal' && (
                              <span className="text-blue-400 ml-2">({expense.headcount || 1} pers.)</span>
                            )}
                          </TableCell>
                          {amounts.map((amount, i) => (
                            <TableCell key={i} className={cn(
                              "text-center tabular-nums",
                              amount === 0 && "text-muted-foreground"
                            )}>
                              {formatTableValue(amount, currency)}
                            </TableCell>
                          ))}
                          <TableCell className={cn(
                            "text-center tabular-nums font-bold bg-primary/10",
                            cagr > 0 ? "text-primary" : "text-muted-foreground"
                          )}>
                            {cagr > 0 ? `+${cagr.toFixed(1)}%` : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Category Totals */}
                    {(selectedCategory === 'all' ? CATEGORIES : [selectedCategory])
                      .filter(cat => expenses.some(e => e.category === cat))
                      .map(cat => (
                      <TableRow key={cat} className="bg-secondary/40 font-bold border-t-2 border-border">
                        <TableCell colSpan={2}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[cat] }} />
                            SUBTOTAL {cat.toUpperCase()}
                          </div>
                        </TableCell>
                        {Array.from({ length: years }, (_, i) => (
                          <TableCell key={i} className="text-center tabular-nums">
                            {formatTableValue(yearlyBreakdown[i]?.[cat] || 0, currency)}
                          </TableCell>
                        ))}
                        <TableCell className="text-center tabular-nums text-primary">
                          {calculateCagr(cat) > 0 ? `+${calculateCagr(cat).toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Grand Total */}
                    <TableRow className="bg-primary/20 font-bold">
                      <TableCell colSpan={2} className="text-primary">TOTAL GASTOS FIJOS</TableCell>
                      {yearlyBreakdown.map((yd, i) => {
                        const total = selectedCategory === 'all' 
                          ? CATEGORIES.reduce((sum, cat) => sum + (yd[cat] || 0), 0)
                          : yd[selectedCategory] || 0;
                        return (
                          <TableCell key={i} className="text-center tabular-nums text-primary">
                            {formatCurrency(total, currency)}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center tabular-nums text-primary">
                        {(() => {
                          const y1 = calculateYearlyTotal(1);
                          const yL = calculateYearlyTotal(years);
                          const n = years - 1;
                          const cagr = y1 > 0 && n > 0 ? (Math.pow(yL / y1, 1 / n) - 1) * 100 : 0;
                          return `${cagr > 0 ? '+' : ''}${cagr.toFixed(1)}%`;
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goToPrev}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button onClick={goToNext} data-testid="continue-btn">
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <BlockerDialog showDialog={showDialog} onSave={handleSaveAndProceed} onDiscard={handleDiscard} onCancel={handleCancel} saving={saving} />
    </div>
  );
};
